import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { observer } from 'mobx-react';
import { formatMessage } from '../../../translations/format-message';
import { FormattedMessage } from 'react-intl';
import { useLedSerial } from '../hooks/use-led-serial';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';

interface LEDCutProps {
  ledCutList: any[];
  isTimerRunning: boolean;
  setLedCutListState: React.Dispatch<React.SetStateAction<any[]>>;
}

const formatCuttingLength = (val: any) => {
  if (val === null || val === undefined || val === '') return '';
  const num = Number(val);
  // DB value is in mm (e.g. "103.0000"); display it without the decimal part (-> "103")
  return !isNaN(num) ? String(Math.trunc(num)) : String(val);
};

const LEDCut: React.FC<LEDCutProps> = ({
  isTimerRunning,
  ledCutList,
  setLedCutListState,
}) => {
  const { sendCommand } = useLedSerial();
  const { productStore } = useStore();
  const { GetFactorySettingService } = productStore;

  useEffect(() => {
    const tenantId = Number(getTenantIdFromLocalStorage());
    if (tenantId) {
      GetFactorySettingService(tenantId);
    }
  }, [GetFactorySettingService]);

  /**
   * Send LED values to the device and show the acknowledgement/error
   */
  const handleLEDClick = async (item: any, time: number) => {
    try {
      const tenantId = Number(getTenantIdFromLocalStorage());
      let limit = productStore.ledLength;
      if (tenantId) {
        const fetched = await GetFactorySettingService(tenantId);
        if (typeof fetched === 'number') {
          limit = fetched;
        } else {
          limit = productStore.ledLength;
        }
      }

      const numericLength = Number(item.MaterialCuttingLength) || 0;
      // Cutting length is in mm; the device and the factory limit are in cm
      // (1000 = 10 m). Convert mm -> cm, rounding any fraction up
      // (3 mm -> 1, 103 mm -> 11).
      const length = Math.ceil(numericLength / 10);

      if (limit > 0 && length > limit) {
        toast.error(formatMessage('led_error_length_out_of_bounds'));
        return;
      }

      /**
       * Example:
       * 1000,30,G,100,R,150,G,200
       */
      const message = `${limit},${time},R,${length}\r\n`;

      const response = await sendCommand(message);
      console.log('LED Cut device response:', response);

      toast.success(formatMessage('led_command_success'));
    } catch (error: any) {
      console.error('Serial error:', error);
      toast.error(error?.message || formatMessage('led_error_unknown'));
    }
  };

  return (
    <div className="tableDevices product-img tableDesign">
      {ledCutList && ledCutList.length > 0 ? (
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table table-hover table-bordered">
              <thead>
                <tr>
                  <th>{formatMessage('led_title')}</th>
                  <th>{formatMessage('material_product_id')}</th>
                  <th>{formatMessage('m_katashiki_1')}</th>
                  <th>{formatMessage('m_katashiki_2')}</th>
                  <th>{formatMessage('manufacturer_name')}</th>
                  <th>{formatMessage('material_cutting_length')} (mm)</th>
                </tr>
              </thead>

              <tbody>
                {ledCutList.map((item, index) => (
                  <tr key={`${index + 1}`}>
                    <td>
                      <Button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleLEDClick(item, -1)}
                        disabled={!isTimerRunning}
                      >
                        <FormattedMessage id="led_title" />
                      </Button>
                    </td>

                    <td>{item.MaterialProductId}</td>

                    <td>{item.katashiki1}</td>

                    <td>{item.katashiki2}</td>

                    <td>{item.ManufacturerName}</td>

                    <td>
                      {formatCuttingLength(
                        item.MaterialCuttingLength
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-row d-flex">
          <span className="empty-message text-center w-100">
            {formatMessage('no_bom_data_available')}
          </span>
        </div>
      )}
    </div>
  );
};

export default observer(LEDCut);